import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowLeft, ChevronRight, Download, ExternalLink, File, Folder, GitCommitHorizontal, RefreshCw, } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, } from 'react';
import { languageFromPath } from '../lib/diff.js';
import { formatBytes } from '../lib/format.js';
import { highlight, isHighlightable } from '../lib/highlight.js';
import { Markdown } from '../lib/markdown.js';
import { CopyButton } from './CodeBlock.js';
import { DiffView } from './DiffView.js';
import { cx, useViewer } from './primitives.js';
import { Sheet } from './Sheet.js';
const PASSIVE_IMAGE = /^image\/(png|jpeg|gif|webp|avif|bmp)$/;
const PASSIVE_VIDEO = /^video\/(mp4|webm)$/;
const HIGHLIGHT_MAX_LINES = 4000;
/** Applied inside every sandboxed frame too (defence in depth over the daemon's own). */
export const SNAPSHOT_CSP = "default-src 'none'; img-src data:; media-src data:; style-src 'unsafe-inline'; font-src data:";
function useRead(store, read, nonce = 0) {
    const key = read ? JSON.stringify(read) : '';
    const [state, setState] = useState({
        key: '',
        nonce: -1,
        loading: true,
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by the serialized request
    useEffect(() => {
        if (!read)
            return;
        let cancelled = false;
        setState({ key, nonce, loading: true });
        store.read(read, nonce > 0).then((data) => !cancelled && setState({ key, nonce, data, loading: false }), (e) => !cancelled &&
            setState({
                key,
                nonce,
                loading: false,
                error: e instanceof Error ? e.message : 'Could not open this reference.',
            }));
        return () => {
            cancelled = true;
        };
    }, [store, key, nonce]);
    return state.key === key && state.nonce === nonce ? state : { loading: true };
}
function bytesOf(d) {
    if (!d)
        return undefined;
    return 'bytes' in d ? d.bytes : new TextEncoder().encode(d.text);
}
function textOf(d) {
    if (!d)
        return undefined;
    return 'text' in d ? d.text : new TextDecoder().decode(d.bytes);
}
/** Blob URL for passive media only; revoked on change/unmount. */
function useObjectUrl(bytes, type) {
    const [url, setUrl] = useState();
    useEffect(() => {
        if (!bytes || !type || typeof URL.createObjectURL !== 'function')
            return;
        const u = URL.createObjectURL(new Blob([bytes], { type }));
        setUrl(u);
        return () => {
            URL.revokeObjectURL(u);
            setUrl(undefined);
        };
    }, [bytes, type]);
    return url;
}
function DownloadButton({ bytes, name }) {
    if (!bytes)
        return null;
    return (_jsxs("button", { type: "button", className: "vk-btn vk-btn--secondary vk-btn--sm", onClick: () => {
            // Always octet-stream: a downloaded file never opens as an app-origin document.
            const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = name;
            a.rel = 'noopener';
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }, children: [_jsx(Download, { size: 14, "aria-hidden": true }), " Download"] }));
}
// ---------------------------------------------------------------------------------------------
// Code view: line numbers, highlighted range, jump to line
// ---------------------------------------------------------------------------------------------
export function RefCode({ code, lang, line, lineEnd, }) {
    const lines = useMemo(() => code.split('\n'), [code]);
    const [tokens, setTokens] = useState(null);
    const pre = useRef(null);
    useEffect(() => {
        setTokens(null);
        if (!lang || !isHighlightable(lang) || lines.length > HIGHLIGHT_MAX_LINES)
            return;
        let cancelled = false;
        highlight(code, lang)
            .then((t) => !cancelled && setTokens(t))
            .catch(() => { });
        return () => {
            cancelled = true;
        };
    }, [code, lang, lines.length]);
    const from = line ?? 0;
    const to = lineEnd ?? line ?? 0;
    useLayoutEffect(() => {
        if (!line)
            return;
        const el = pre.current?.querySelector(`[data-line="${line}"]`);
        el?.scrollIntoView?.({ block: 'center' });
    }, [line]);
    return (_jsx("pre", { ref: pre, className: "vk-refcode", tabIndex: 0, children: _jsx("code", { children: lines.map((text, i) => {
                const n = i + 1;
                const t = tokens?.[i];
                return (_jsxs("span", { className: cx('vk-refcode__line', n >= from && n <= to && 'is-mark'), "data-line": n, children: [_jsx("span", { className: "vk-refcode__num", "aria-hidden": "true", children: n }), _jsx("span", { className: "vk-refcode__text", children: t
                                ? t.map((tok, j) => (_jsx("span", { style: tok.light || tok.dark
                                        ? { '--sl': tok.light, '--sd': tok.dark }
                                        : undefined, children: tok.content }, j)))
                                : text }), '\n'] }, i));
            }) }) }));
}
/** HTML in an opaque-origin, script-less frame with a no-network CSP. */
export function SandboxFrame({ html, title }) {
    const { renderHtml } = useViewer();
    const doc = `<!doctype html><meta http-equiv="Content-Security-Policy" content="${SNAPSHOT_CSP}">${html}`;
    if (renderHtml)
        return _jsx(_Fragment, { children: renderHtml({ html: doc, title }) });
    return (_jsx("iframe", { className: "vk-refframe", title: title, sandbox: "", referrerPolicy: "no-referrer", srcDoc: doc }));
}
// ---------------------------------------------------------------------------------------------
// Bodies per target type
// ---------------------------------------------------------------------------------------------
function basename(p) {
    return (p
        .replace(/[\\/]$/, '')
        .split(/[\\/]/)
        .pop() ||
        p ||
        'repository');
}
function extOf(p) {
    const b = basename(p);
    const i = b.lastIndexOf('.');
    return i > 0 ? b.slice(i + 1).toLowerCase() : '';
}
function Status({ children }) {
    return (_jsx("p", { className: "vk-refinspect__status", role: "status", children: children }));
}
function FileContent({ file, line, lineEnd, }) {
    const ext = extOf(file.path);
    const [rendered, setRendered] = useState(ext === 'md' || ext === 'mdx' || ext === 'markdown');
    const [htmlOn, setHtmlOn] = useState(false);
    const bytes = bytesOf(file.data);
    const previewBytes = bytesOf(file.previewData);
    const mime = file.mime.split(';')[0]?.trim().toLowerCase() ?? '';
    const imageType = PASSIVE_IMAGE.test(mime) ? mime : undefined;
    const videoType = PASSIVE_VIDEO.test(mime) ? mime : undefined;
    const previewType = file.previewMime && PASSIVE_IMAGE.test(file.previewMime) ? file.previewMime : undefined;
    const imageUrl = useObjectUrl(imageType ? bytes : undefined, imageType);
    const videoUrl = useObjectUrl(videoType ? bytes : undefined, videoType);
    const previewUrl = useObjectUrl(previewType ? previewBytes : undefined, previewType);
    const name = basename(file.path);
    if (!file.data && !file.previewData)
        return (_jsx(Status, { children: file.tooLarge
                ? `Too large to open here (${formatBytes(file.size)}).`
                : 'Nothing to show for this file.' }));
    if (mime === 'image/svg+xml' && bytes) {
        // <img> never runs SVG script; the data URL keeps it out of the app origin.
        let bin = '';
        for (const b of bytes)
            bin += String.fromCharCode(b);
        return (_jsx("figure", { className: "vk-refmedia", children: _jsx("img", { src: `data:image/svg+xml;base64,${btoa(bin)}`, alt: name }) }));
    }
    if (imageType || (previewType && !file.text))
        return (_jsxs("figure", { className: "vk-refmedia", children: [imageUrl || previewUrl ? (_jsx("img", { src: imageUrl ?? previewUrl, alt: name })) : (_jsx(Status, { children: "Loading image\u2026" })), mime === 'application/pdf' ? (_jsxs("figcaption", { className: "vk-meta", children: ["First page. ", _jsx(DownloadButton, { bytes: bytes, name: name })] })) : !imageType && previewType ? (_jsxs("figcaption", { className: "vk-meta", children: ["Downscaled preview \u00B7 original ", formatBytes(file.size)] })) : null] }));
    if (videoType)
        return (_jsx("figure", { className: "vk-refmedia", children: _jsx("video", { src: videoUrl, controls: true, preload: "metadata" }) }));
    if (!file.text)
        return (_jsxs("div", { className: "vk-refinspect__binary", children: [_jsxs(Status, { children: ["Binary file (", mime || 'unknown type', ", ", formatBytes(file.size), ")."] }), _jsx(DownloadButton, { bytes: bytes, name: name })] }));
    const text = textOf(file.data) ?? '';
    const isMd = ext === 'md' || ext === 'mdx' || ext === 'markdown';
    const isHtml = ext === 'html' || ext === 'htm';
    let code = text;
    let lang = languageFromPath(file.path);
    if (ext === 'json' || mime === 'application/json') {
        try {
            code = JSON.stringify(JSON.parse(text), null, 2);
        }
        catch { }
        lang = 'json';
    }
    return (_jsxs(_Fragment, { children: [isMd || isHtml ? (_jsxs("div", { className: "vk-seg", role: "group", "aria-label": "View as", children: [_jsx("button", { type: "button", "aria-pressed": isMd ? rendered : htmlOn, onClick: () => (isMd ? setRendered(true) : setHtmlOn(true)), children: isMd ? 'Rendered' : 'Page (sandboxed)' }), _jsx("button", { type: "button", "aria-pressed": isMd ? !rendered : !htmlOn, onClick: () => (isMd ? setRendered(false) : setHtmlOn(false)), children: "Source" })] })) : null, file.tooLarge ? (_jsxs(Status, { children: ["Showing the first part of a large file (", formatBytes(file.size), ")."] })) : null, file.scrubbed ? (_jsx(Status, { children: "Secrets detected in this file were redacted on the machine." })) : null, isMd && rendered ? (_jsx(Markdown, { text: text, className: "vk-refinspect__md" })) : isHtml && htmlOn ? (_jsx(SandboxFrame, { html: text, title: `${name} (sandboxed, scripts off)` })) : (_jsx(RefCode, { code: code, lang: lang, line: line, lineEnd: lineEnd }))] }));
}
function FileView({ store, target, line, lineEnd, rev: initialRev, }) {
    const [mode, setMode] = useState(initialRev ? 'rev' : 'current');
    const [rev, setRev] = useState(initialRev ?? '');
    const [draft, setDraft] = useState(initialRev ?? 'HEAD~1');
    const read = mode === 'diff'
        ? { type: 'diff', path: target.path }
        : mode === 'rev' && !rev
            ? null
            : {
                type: 'file',
                path: target.path,
                root: target.root,
                ...(mode === 'rev' && rev ? { rev } : {}),
            };
    const r = useRead(store, read);
    const file = r.data?.type === 'file' ? r.data : undefined;
    return (_jsxs("div", { className: "vk-refinspect__file", children: [_jsxs("div", { className: "vk-refinspect__toolbar", children: [_jsxs("div", { className: "vk-seg", role: "group", "aria-label": "Version", children: [_jsx("button", { type: "button", "aria-pressed": mode === 'current', onClick: () => setMode('current'), children: "Current" }), _jsx("button", { type: "button", "aria-pressed": mode === 'diff', onClick: () => setMode('diff'), children: "Diff vs base" }), _jsx("button", { type: "button", "aria-pressed": mode === 'rev', onClick: () => setMode('rev'), children: "At commit" })] }), mode === 'rev' ? (_jsxs("form", { className: "vk-refinspect__rev", onSubmit: (e) => {
                            e.preventDefault();
                            setRev(draft.trim());
                        }, children: [_jsxs("label", { children: [_jsx("span", { className: "vk-sr", children: "Commit or branch" }), _jsx("input", { className: "vk-input", value: draft, onChange: (e) => setDraft(e.target.value), placeholder: "sha, branch or HEAD~1", spellCheck: false })] }), _jsx("button", { type: "submit", className: "vk-btn vk-btn--secondary vk-btn--sm", children: "Show" })] })) : null, _jsx("span", { className: "vk-refinspect__spacer" }), file?.text && file.data ? (_jsx(CopyButton, { text: textOf(file.data) ?? '', label: "Copy contents" })) : null] }), r.error ? (_jsx(Status, { children: r.error })) : r.loading || !r.data ? (read ? (_jsx(Status, { children: "Opening\u2026" })) : (_jsx(Status, { children: "Enter a commit or branch." }))) : r.data.type === 'diff' ? (r.data.files.length ? (_jsx(DiffView, { files: r.data.files, title: `Changes vs ${r.data.base}`, maxRowsPerFile: 400 })) : (_jsxs(Status, { children: ["No changes to this file compared with ", r.data.base, "."] }))) : file ? (_jsx(FileContent, { file: file, line: line, lineEnd: lineEnd })) : null] }));
}
function DirView({ store, target, }) {
    const r = useRead(store, { type: 'dir', path: target.path, root: target.root });
    if (r.error)
        return _jsx(Status, { children: r.error });
    if (r.loading || r.data?.type !== 'dir')
        return _jsx(Status, { children: "Opening\u2026" });
    const dir = r.data;
    const join = (e) => (dir.path ? `${dir.path.replace(/\/$/, '')}/${e.name}` : e.name);
    return (_jsxs(_Fragment, { children: [_jsx("ul", { className: "vk-refdir", "aria-label": `Contents of ${dir.path || 'repository root'}`, children: dir.entries.map((e) => (_jsx("li", { children: _jsxs("button", { type: "button", className: "vk-refdir__item", onClick: () => store.push({
                            kind: 'target',
                            target: e.type === 'dir'
                                ? { type: 'dir', path: join(e), root: dir.root, entries: 0 }
                                : {
                                    type: 'file',
                                    path: join(e),
                                    root: dir.root,
                                    size: e.size ?? 0,
                                    mime: '',
                                },
                        }), children: [e.type === 'dir' ? _jsx(Folder, { size: 16, "aria-hidden": true }) : _jsx(File, { size: 16, "aria-hidden": true }), _jsxs("span", { className: "vk-trunc", children: [e.name, e.type === 'dir' ? '/' : ''] }), e.type === 'file' && e.size != null ? (_jsx("span", { className: "vk-meta", children: formatBytes(e.size) })) : (_jsx(ChevronRight, { size: 14, "aria-hidden": true }))] }) }, e.name))) }), dir.truncated ? _jsxs(Status, { children: ["Showing the first ", dir.entries.length, " entries."] }) : null, !dir.entries.length ? _jsx(Status, { children: "This folder is empty." }) : null] }));
}
function CommitView({ store, sha }) {
    const r = useRead(store, { type: 'commit', sha });
    if (r.error)
        return _jsx(Status, { children: r.error });
    if (r.loading || r.data?.type !== 'commit')
        return _jsx(Status, { children: "Opening\u2026" });
    const c = r.data;
    const [subject, ...body] = c.message.split('\n');
    return (_jsxs("div", { className: "vk-refcommit", children: [_jsxs("header", { className: "vk-refcommit__head", children: [_jsx("p", { className: "vk-refcommit__subject", children: subject }), _jsxs("p", { className: "vk-meta", children: [_jsx("code", { children: c.sha.slice(0, 12) }), " \u00B7 ", c.author, " \u00B7 ", new Date(c.date).toLocaleString(), _jsx(CopyButton, { text: c.sha, label: "Copy SHA" })] }), body.join('\n').trim() ? (_jsx("pre", { className: "vk-refcommit__body", children: body.join('\n').trim() })) : null] }), _jsx("ul", { className: "vk-refdir", "aria-label": "Files in this commit", children: c.files.map((f) => (_jsx("li", { children: _jsxs("button", { type: "button", className: "vk-refdir__item", onClick: () => store.push({
                            kind: 'target',
                            target: {
                                type: 'file',
                                path: f.movePath ?? f.path,
                                root: 'worktree',
                                size: 0,
                                mime: '',
                            },
                            rev: c.sha,
                        }), children: [_jsx(File, { size: 16, "aria-hidden": true }), _jsx("span", { className: "vk-trunc", children: f.movePath ?? f.path }), _jsxs("span", { className: "vk-meta", children: ["at ", c.sha.slice(0, 7)] })] }) }, f.path))) }), c.files.length ? _jsx(DiffView, { files: c.files, title: "Commit changes" }) : null, c.truncated ? _jsx(Status, { children: "The diff was truncated; open files individually." }) : null] }));
}
// ---- live-preview-tunnel: Live (E2E tunnel to the machine) | Snapshot ----
function PreviewView({ store, url }) {
    const { renderLivePreview } = useViewer();
    const [mode, setMode] = useState(renderLivePreview ? 'live' : 'snapshot');
    if (!renderLivePreview)
        return _jsx(SnapshotView, { store: store, url: url });
    return (_jsxs("div", { className: "vk-refpreview", children: [_jsx("div", { className: "vk-seg", role: "group", "aria-label": "Preview mode", children: ['live', 'snapshot'].map((m) => (_jsx("button", { type: "button", "aria-pressed": mode === m, onClick: () => setMode(m), children: m === 'live' ? 'Live' : 'Snapshot' }, m))) }), mode === 'live' ? (renderLivePreview({
                url,
                title: `Live preview of ${url}`,
                snapshot: () => _jsx(SnapshotView, { store: store, url: url }),
            })) : (_jsx(SnapshotView, { store: store, url: url }))] }));
}
// ---- end live-preview-tunnel ----
function SnapshotView({ store, url }) {
    const [nonce, setNonce] = useState(0);
    const r = useRead(store, { type: 'preview', url }, nonce);
    const p = r.data?.type === 'preview' ? r.data : undefined;
    return (_jsxs("div", { className: "vk-refpreview", children: [_jsxs("div", { className: "vk-refinspect__toolbar", children: [_jsx("span", { className: "vk-refpreview__label", children: p
                            ? `Snapshot · ${new Date(p.capturedAt).toLocaleTimeString()} · scripts off${p.omitted ? ` · ${p.omitted} assets left out` : ''}`
                            : 'Snapshot' }), _jsx("span", { className: "vk-refinspect__spacer" }), _jsxs("button", { type: "button", className: "vk-btn vk-btn--secondary vk-btn--sm", onClick: () => setNonce((n) => n + 1), children: [_jsx(RefreshCw, { size: 14, "aria-hidden": true }), " Refresh"] }), _jsxs("a", { className: "vk-btn vk-btn--secondary vk-btn--sm", href: url, target: "_blank", rel: "noopener noreferrer", children: [_jsx(ExternalLink, { size: 14, "aria-hidden": true }), " Open original"] })] }), _jsx("p", { className: "vk-meta", children: "The original address only opens on the run\u2019s machine. This is a private snapshot of the files it serves." }), r.error ? (_jsx(Status, { children: r.error })) : p ? (_jsx(SandboxFrame, { html: p.html, title: `Snapshot of ${url}` })) : (_jsx(Status, { children: "Capturing snapshot\u2026" }))] }));
}
function Chooser({ store, item, }) {
    return (_jsxs(_Fragment, { children: [_jsxs("p", { className: "vk-meta", children: [item.matches.length, " files match ", _jsx("code", { className: "vk-md-code", children: item.label }), ". Pick one:"] }), _jsx("ul", { className: "vk-refdir", "aria-label": "Matching files", children: item.matches.map((m) => {
                    const path = m.type === 'file' || m.type === 'dir' ? m.path : m.type;
                    return (_jsx("li", { children: _jsxs("button", { type: "button", className: "vk-refdir__item", onClick: () => store.replace({
                                kind: 'target',
                                target: m,
                                ...(item.line ? { line: item.line } : {}),
                                ...(item.lineEnd ? { lineEnd: item.lineEnd } : {}),
                            }), children: [m.type === 'dir' ? (_jsx(Folder, { size: 16, "aria-hidden": true })) : (_jsx(File, { size: 16, "aria-hidden": true })), _jsx("span", { className: "vk-trunc", children: path }), 'root' in m && m.root === 'main' ? (_jsx("span", { className: "vk-meta", children: "main checkout" })) : null] }) }, `${m.type}:${path}:${'root' in m ? m.root : ''}`));
                }) })] }));
}
function Breadcrumbs({ store, path, root, }) {
    const parts = path.split('/').filter(Boolean);
    const open = (i) => store.push({
        kind: 'target',
        target: { type: 'dir', path: parts.slice(0, i).join('/'), root, entries: 0 },
    });
    return (_jsxs("nav", { className: "vk-refcrumbs", "aria-label": "Location", children: [_jsx("button", { type: "button", onClick: () => open(0), children: root === 'main' ? 'main checkout' : root === 'assets' ? 'asset folders' : 'worktree' }), parts.slice(0, -1).map((p, i) => (_jsxs("span", { className: "vk-refcrumbs__seg", children: [_jsx(ChevronRight, { size: 12, "aria-hidden": true }), _jsx("button", { type: "button", onClick: () => open(i + 1), children: p })] }, i)))] }));
}
function titleOf(item) {
    if (item.kind === 'artifact')
        return { title: 'Artifact' };
    if (item.kind === 'choose')
        return { title: `Which ${item.label}?` };
    const t = item.target;
    switch (t.type) {
        case 'file':
            return {
                title: `${basename(t.path)}${item.line ? `:${item.line}${item.lineEnd ? `-${item.lineEnd}` : ''}` : ''}${item.rev ? ` @ ${item.rev.slice(0, 10)}` : ''}`,
                copy: `${t.path}${item.line ? `:${item.line}` : ''}`,
            };
        case 'dir':
            return { title: `${basename(t.path)}/`, copy: t.path };
        case 'commit':
            return { title: `Commit ${t.sha.slice(0, 7)}`, copy: t.sha, copyLabel: 'Copy SHA' };
        case 'branch':
            return { title: `Branch ${t.name}`, copy: t.name, copyLabel: 'Copy branch name' };
        case 'preview':
            return { title: `Preview ${t.path || '/'}`, copy: t.url, copyLabel: 'Copy address' };
    }
}
function Body({ store, item }) {
    const { artifactById, renderArtifact } = useViewer();
    if (item.kind === 'artifact') {
        const a = artifactById?.(item.artifactId);
        return a && renderArtifact ? (renderArtifact(a, { variant: 'card' })) : (_jsx(Status, { children: "This artifact is not available on this device." }));
    }
    if (item.kind === 'choose')
        return _jsx(Chooser, { store: store, item: item });
    const t = item.target;
    switch (t.type) {
        case 'file':
            return (_jsxs(_Fragment, { children: [_jsx(Breadcrumbs, { store: store, path: t.path, root: t.root }), _jsx(FileView, { store: store, target: t, line: item.line, lineEnd: item.lineEnd, rev: item.rev }, `${t.root}:${t.path}:${item.rev ?? ''}`)] }));
        case 'dir':
            return (_jsxs(_Fragment, { children: [_jsx(Breadcrumbs, { store: store, path: t.path, root: t.root }), _jsx(DirView, { store: store, target: t })] }));
        case 'commit':
            return _jsx(CommitView, { store: store, sha: t.sha });
        case 'branch':
            return (_jsxs("div", { className: "vk-refcommit", children: [_jsxs("p", { children: ["Branch ", _jsx("code", { className: "vk-md-code", children: t.name }), " is at", ' ', _jsx("code", { className: "vk-md-code", children: t.sha.slice(0, 12) }), "."] }), _jsxs("button", { type: "button", className: "vk-btn vk-btn--secondary vk-btn--sm", onClick: () => store.push({ kind: 'target', target: { type: 'commit', sha: t.sha, subject: '' } }), children: [_jsx(GitCommitHorizontal, { size: 14, "aria-hidden": true }), " Open its latest commit"] })] }));
        case 'preview':
            return _jsx(PreviewView, { store: store, url: t.url });
    }
}
/** Mounted once per viewer when a resolver is present. */
export function RefInspector({ store }) {
    useSyncExternalStore(store.subscribe, store.getVersion, store.getVersion);
    const item = store.current;
    if (!item)
        return null;
    const { title, copy, copyLabel = 'Copy path' } = titleOf(item);
    return (_jsx(Sheet, { open: true, onClose: () => store.close(), placement: "side", size: "lg", title: title, actions: _jsxs("span", { className: "vk-row-gap", children: [store.depth > 1 ? (_jsx("button", { type: "button", className: "vk-iconbtn vk-iconbtn--lg", "aria-label": "Back", title: "Back", onClick: () => store.back(), children: _jsx(ArrowLeft, { size: 18 }) })) : null, copy ? _jsx(CopyButton, { text: copy, label: copyLabel }) : null] }), children: _jsx("div", { className: "vk-refinspect", children: _jsx(Body, { store: store, item: item }) }) }));
}
//# sourceMappingURL=RefInspector.js.map